// BuildingGeometryAccessors.ts — the mobx-graphlink accessor layer for the Buildings Set1 P2
// footprint / level-stacking / opening-binding slice (§3.4–3.6). These are reactive (any relevant
// DB change re-runs them) and return PLAIN data only (no `three` imports). The pure, Node-testable
// geometry engines live in `Building/*_Pure.ts`; this file does the DB-dependent collection and
// resolution (which object's points, which descendants, resolved params, world→building pose) and
// then hands off to them.
import {CreateAccessor} from "mobx-graphlink";
import {GetMapObject} from "../mapObjects.js";
import {MapObject} from "./@MapObject.js";
import {GetObjAncestors, GetObjDescendants, ResolveObjPosInWorld, ResolveObjRotInWorld} from "./$mapObject.js";
import {type Vec2Simple} from "../../../Utils/ThreeJS/VectorStructs.js";
import {GetPointsMatchingShape} from "../../../Utils/ThreeJS/ShapeUtils.js";
import {ResolvePathMeshes} from "./@MapObject/ObjPath/ObjPathResolvers.js";
import {ObjMesh} from "./@MapObject/ObjMesh.js";
import {ObjPath} from "./@MapObject/ObjPath.js";
import {Mod_Room, RoomRoofContribution} from "./@MapObject/ObjModifiers/Mod_Room.js";
import {Mod_Level} from "./@MapObject/ObjModifiers/Mod_Level.js";
import {Mod_Window} from "./@MapObject/ObjModifiers/Mod_Window.js";
import {Mod_Door} from "./@MapObject/ObjModifiers/Mod_Door.js";
import {Mod_Opening} from "./@MapObject/ObjModifiers/Mod_Opening.js";
import {Mod_Building} from "./@MapObject/ObjModifiers/Mod_Building.js";
import {BuildingShapeSourceKind} from "./@MapObject/ObjModifiers/Building/BuildingShared.js";
import {
	FindOwningBuilding, GetEnabledMod,
	ResolveLevelParams, ResolveWindowParams, ResolveDoorParams, ResolveOpeningParams, ResolveRoofParams,
} from "./BuildingAccessors.js";
import {
	NormalizeFootprint, TransformPointsToFrame, FootprintAABB, AABBPolygon,
	IsRectilinearRotation, type Pose2D,
} from "./Building/BuildingFootprint_Pure.js";
import {ResolveBuildingFloors_Pure, type FloorClaim} from "./Building/BuildingFloors_Pure.js";
import {
	BindOpeningsToFloorWalls_Pure, type FloorWallFace, type FloorOpeningCandidate,
} from "./Building/BuildingOpenings_Pure.js";
import type {
	FloorSlot, BoundOpening, FloorWallSegment, FloorOpeningsBySegment,
	BuildingGeometryPlan, BuildingFloorPlan, BuildingRoomInstance, ResolvedRoomMaterials,
} from "./Building/BuildingGeometry.js";
import {HARDCODED_BUILDING_DEFAULTS} from "./@MapObject/ObjModifiers/Building/BuildingDefaults_Hardcoded.js";
import {ResolveRoomParams, ResolveBuildingMaterials} from "./BuildingAccessors.js";
import {CenterlineToWallRings} from "../../../Building/Geometry/Offset_Pure.js";
import {MergeFloorWalls_Pure, type FloorWallPlan} from "../../../Building/Geometry/BuildingWalls_Pure.js";
import {ComputeFloorGeometryKey, DetectFloorMaterialConflicts_Pure} from "../../../Building/Geometry/BuildingGeometryKey_Pure.js";
import {ResolveRoofPlan_Pure, type ResolveRoofPlan_Input, type RoofRect, type RoofCluster} from "../../../Building/Geometry/Roof_Pure.js";
import type {MeshPlan} from "../../../Building/Geometry/MeshPlan.js";

// ---------------------------------------------------------------------------
// Pose helpers
// ---------------------------------------------------------------------------
/** The ordered list of 2D poses from `obj` (inclusive) up to (but NOT including) `stopObjID`.
 *  When `stopObjID` is null, walks all the way to the root. The returned poses are ordered
 *  child-first so `TransformPointsToFrame` applies them in ancestor order to express `obj`-local
 *  content in the stop-object's frame. */
function BuildPoseChainTo(obj: MapObject, stopObjID: string|n): Pose2D[] {
	const chain: Pose2D[] = [];
	const seen = new Set<string>();
	let cur: MapObject|n = obj;
	while (cur != null && cur.id != stopObjID) {
		if (seen.has(cur.id)) break; // guard against cycles
		seen.add(cur.id);
		chain.push({rotationDeg: cur.rotation ?? 0, position: [cur.position.x, cur.position.y]});
		const parentID = cur.parent;
		if (parentID == null) break;
		const parent = GetObjAncestors(cur).find(a=>a.id == parentID) ?? null;
		cur = parent;
	}
	return chain; // child-first
}

// ---------------------------------------------------------------------------
// §3.4 Footprints
// ---------------------------------------------------------------------------
/** Resolve a Room's footprint in the ROOM object's LOCAL frame, CCW-normalized + closed (interior face). Returns null when the room has no resolvable shape/mesh/path source. Set2 §2: these points are now the wall CENTERLINE, not the interior face — the plumbing is unchanged (same authored outline), only the ring's meaning; `ResolveRoomWallRings` turns it into the two wall faces. */
export const ResolveRoomFootprint_Local = CreateAccessor((obj: MapObject, mod: Mod_Room|n = null): Vec2Simple[]|n=>{
	const m = mod ?? GetEnabledMod(obj, "room") as Mod_Room|n;
	if (m == null) return null;

	const source = m.shapeSource;
	// Auto-resolve: shape → first mesh → first path (when shapeSource null).
	if (source == null || source.kind == BuildingShapeSourceKind.shape) {
		if (obj.shape_type != null) {
			const size = obj.shape_size ? [obj.shape_size.x, obj.shape_size.y] as [number, number] : null;
			return NormalizeFootprint(GetPointsMatchingShape(size, obj.shape_type as string as "ellipse"|"rectangle"), true);
		}
		if (source != null && source.kind == BuildingShapeSourceKind.shape) return null; // explicitly shape, but no shape_type
		// else fall through to mesh/path auto-resolve
	}

	if (source == null || source.kind == BuildingShapeSourceKind.mesh) {
		const index = source?.index ?? 0;
		const mesh = (obj.meshes ?? [])[index] as ObjMesh|n;
		if (mesh && mesh.points && mesh.points.length >= 3) {
			return NormalizeFootprint(mesh.points, true);
		}
		if (source != null && source.kind == BuildingShapeSourceKind.mesh) return null; // explicit mesh index out of range
	}

	if (source == null || source.kind == BuildingShapeSourceKind.path) {
		const index = source?.index ?? 0;
		const path = (obj.paths ?? [])[index] as ObjPath|n;
		if (path) {
			const resolved = ResolvePathMeshes(path, obj);
			const mesh = resolved?.meshes?.[0];
			if (mesh && resolved?.closed && mesh.length >= 3) {
				return NormalizeFootprint(mesh, true);
			}
			// Open path (or unresolvable) → rejected for a room footprint.
			return null;
		}
	}
	return null;
});

/** The Room's footprint transformed into the BUILDING's local frame (the wall CENTERLINE under
 *  Set2 §2). Returns `{points, rectilinear: boolean}` where `rectilinear` is false when the room's
 *  rotation relative to the building is not a multiple of 90° (the §3.4 roof union warning).
 *  Returns null when there's no owning building or no resolvable footprint. */
export const ResolveRoomFootprint_InBuilding = CreateAccessor((roomObj: MapObject, buildingObj: MapObject): {
	points: Vec2Simple[];
	rectilinear: boolean;
}|n=>{
	const local = ResolveRoomFootprint_Local(roomObj);
	if (local == null) return null;
	const chain = BuildPoseChainTo(roomObj, buildingObj.id);
	const points = TransformPointsToFrame(local, chain);
	const relRot = (ResolveObjRotInWorld(roomObj) ?? 0) - (ResolveObjRotInWorld(buildingObj) ?? 0);
	return {points, rectilinear: IsRectilinearRotation(relRot)};
});

/** The rectilinear footprint for roof purposes: the room's building-local AABB when its rotation
 *  isn't rectilinear, else the exact building-local outline. Returns null when unresolvable. */
export const ResolveRoomFootprintForRoof = CreateAccessor((roomObj: MapObject, buildingObj: MapObject): Vec2Simple[]|n=>{
	const inBuilding = ResolveRoomFootprint_InBuilding(roomObj, buildingObj);
	if (inBuilding == null) return null;
	if (inBuilding.rectilinear) return inBuilding.points;
	return AABBPolygon(FootprintAABB(inBuilding.points));
});

// ---------------------------------------------------------------------------
// §3.5 Level stacking
// ---------------------------------------------------------------------------
/** Collect every enabled `Mod_Level` in `buildingObj`'s descendants that is NOT inside a nested
 *  non-prototype Building (nested buildings own their own levels). */
export const CollectBuildingLevels = CreateAccessor((buildingObj: MapObject): Array<{obj: MapObject, mod: Mod_Level}>=>{
	const levels: Array<{obj: MapObject, mod: Mod_Level}> = [];
	// Include the building object itself in the subtree: a Level can be co-located with the
	// Building (the common single-room case, §3.1), mirroring `CollectRoomsForLevel`.
	const subtree = [buildingObj, ...GetObjDescendants(buildingObj)];
	const isInsideNestedBuilding = (obj: MapObject): boolean=>{
		const anc = FindOwningBuilding(obj);
		return anc != null && anc.obj.id != buildingObj.id;
	};
	for (const desc of subtree) {
		const mod = GetEnabledMod(desc, "level") as Mod_Level|n;
		if (mod == null) continue;
		if (isInsideNestedBuilding(desc)) continue; // nested building owns its own levels
		levels.push({obj: desc, mod});
	}
	return levels;
});

/** Resolve the building's floors (claimed + gap-bridged) per §3.5. `fallbackStorey` supplies the
 *  storey height used to bridge gaps. Building-local Z; world Z = + ResolveObjPosInWorld(building).z. */
export const ResolveBuildingFloors = CreateAccessor((buildingObj: MapObject): {
	floors: Map<number, FloorSlot>;
	warnings: string[];
}=>{
	const claims: FloorClaim[] = [];
	for (const {obj, mod} of CollectBuildingLevels(buildingObj)) {
		const floorNumbers = (mod.floorNumbers ?? [1]).filter(n=>n != null && Number.isFinite(n));
		const numbers = floorNumbers.length ? floorNumbers : [1];
		const orderKey = obj.orderKey ?? "";
		for (const n of numbers) {
			claims.push({
				floorNumber: n,
				levelObjID: obj.id,
				orderKey,
				resolveHeight: ()=>{ const p = ResolveLevelParams(obj); return {floorThickness: p.floorThickness, wallHeight: p.wallHeight}; },
			});
		}
	}

	// §3.1 implicit level: a `Mod_Level` is OPTIONAL — Building + Room are the required pair. When
	// the building has NO level claims at all (no enabled `Mod_Level` in its subtree) but DOES own
	// room descendants, synthesize an implicit level at floor 1 so those rooms still render. The
	// implicit level is "anchored" to the building object itself: `levelObjID = buildingObj.id`
	// makes `ResolveBuildingGeometryPlan`'s `GetMapObject(floor.levelObjID)` resolve to the
	// building, and `CollectRoomsForLevel(buildingObj)` collects the building's direct rooms
	// (excluding those inside a nested building, which own their own levels). A soft warning notes
	// the implicit level was used.
	let usedImplicitLevel = false;
	if (claims.length == 0) {
		const implicitRooms = CollectRoomsForLevel(buildingObj);
		if (implicitRooms.length) {
			claims.push({
				floorNumber: 1,
				levelObjID: buildingObj.id,
				orderKey: buildingObj.orderKey ?? "",
				resolveHeight: ()=>{ const p = ResolveLevelParams(buildingObj); return {floorThickness: p.floorThickness, wallHeight: p.wallHeight}; },
			});
			usedImplicitLevel = true;
		}
	}

	const result = ResolveBuildingFloors_Pure(claims, {floorThickness: HARDCODED_BUILDING_DEFAULTS.level.floorThickness, wallHeight: HARDCODED_BUILDING_DEFAULTS.level.wallHeight});
	if (usedImplicitLevel) result.warnings.push(`Building "${buildingObj.id}" has no Level modifier; using an implicit level at floor 1 (from the building's own defaults).`);
	return result;
});

// ---------------------------------------------------------------------------
// §5 (Set2 F3) Opening binding — floor-level
// ---------------------------------------------------------------------------
/** Collect the Window/Door/Opening candidate descendants of `roomObjs` (each resolved through its
 *  param chain) with their building-local XY anchors. Shared by `ResolveOpeningsForFloor` and by
 *  `ResolveOpeningBinding` (the ModUI readout). */
function CollectOpeningCandidates(roomObjs: MapObject[], buildingObj: MapObject): FloorOpeningCandidate[] {
	const candidates: FloorOpeningCandidate[] = [];
	const buildingPos = ResolveObjPosInWorld(buildingObj);
	const buildingRot = ResolveObjRotInWorld(buildingObj) ?? 0;
	const roomIDs = new Set(roomObjs.map(r=>r.id));

	for (const roomObj of roomObjs) {
		for (const desc of GetObjDescendants(roomObj)) {
			const winMod = GetEnabledMod(desc, "window") as Mod_Window|n;
			const doorMod = GetEnabledMod(desc, "door") as Mod_Door|n;
			const openingMod = GetEnabledMod(desc, "opening") as Mod_Opening|n;
			if (winMod == null && doorMod == null && openingMod == null) continue;
			const kind = winMod != null ? "window" : (doorMod != null ? "door" : "opening");

			// Ownership: the nearest ancestor Room owns this opening (so a nested room steals its own).
			const owningRoomObj = FindOwningRoomOf(desc);
			if (owningRoomObj == null || !roomIDs.has(owningRoomObj.id)) continue;

			// Anchor in building-local XY: rotate the world-axes offset from the building anchor by
			// the building's world rotation (matches the wall segments' frame).
			const worldPos = ResolveObjPosInWorld(desc);
			if (worldPos == null || buildingPos == null) continue;
			const relRotRad = buildingRot * (Math.PI / 180);
			const dx = worldPos.x - buildingPos.x;
			const dy = worldPos.y - buildingPos.y;
			const cos = Math.cos(relRotRad), sin = Math.sin(relRotRad);
			const localX = dx * cos - dy * sin;
			const localY = dx * sin + dy * cos;

			if (kind == "window") {
				const p = ResolveWindowParams(desc);
				candidates.push({
					objID: desc.id, kind, pos: [localX, localY], width: p.width, height: p.height, sillHeight: p.sillHeight,
					ownerRoomObjID: owningRoomObj.id,
					model: {
						window: {paneDivisionsX: p.paneDivisionsX, paneDivisionsY: p.paneDivisionsY},
						door: {swing: "", openAngle: 0},
					},
				});
			} else if (kind == "door") {
				const p = ResolveDoorParams(desc);
				candidates.push({
					objID: desc.id, kind, pos: [localX, localY], width: p.width, height: p.height, sillHeight: 0,
					ownerRoomObjID: owningRoomObj.id,
					model: {
						window: {paneDivisionsX: 0, paneDivisionsY: 0},
						door: {swing: p.swing, openAngle: p.openAngle},
					},
				});
			} else {
				// Opening: punches the ENTIRE wall (floor slab top → wall top), so no sill/height/model
				// — only `width` is authored. `sillHeight` is 0; `height` = the storey wall height.
				const p = ResolveOpeningParams(desc);
				const wallHeight = ResolveLevelParams(desc).wallHeight;
				candidates.push({
					objID: desc.id, kind, pos: [localX, localY], width: p.width,
					height: wallHeight, sillHeight: 0,
					ownerRoomObjID: owningRoomObj.id,
					model: {
						window: {paneDivisionsX: 0, paneDivisionsY: 0},
						door: {swing: "", openAngle: 0},
					},
				});
			}
		}
	}
	return candidates;
}

/** Set2 §5: bind a FLOOR's openings to the floor's merged wall segments. `roomObjs` are the rooms
 *  on that floor; `walls` is the floor's merged `FloorWallPlan`. The binder runs against each
 *  segment's INTERIOR face (§5 / F1 deviation 2) — the same face `WallSolidBuilder_FloorSegments`
 *  punches, so the hole and the opening model agree (they are a matched pair). */
export const ResolveOpeningsForFloor = CreateAccessor((roomObjs: MapObject[], buildingObj: MapObject, walls: FloorWallPlan|n): {
	openingsBySegment: FloorOpeningsBySegment[];
	warnings: string[];
}=>{
	if (walls == null || walls.segments.length == 0) {
		return {openingsBySegment: [], warnings: []};
	}
	const candidates = CollectOpeningCandidates(roomObjs, buildingObj);
	if (candidates.length == 0) return {openingsBySegment: [], warnings: []};

	// The binder's segment view: the INTERIOR face + the resolved thickness + the claimants.
	// A SHARED wall exposes one face per side; the binder is given the FIRST claimant's face (the
	// same side the wall's normal points away from) so `alongCenter` is measured from a stable end.
	// `alongCenter` is symmetric about the segment centre, so the direction choice only flips which
	// end 0 is — the punch is identical either way (the wall builder uses the same face).
	const faces: FloorWallFace[] = walls.segments.map(s=>({
		innerStart: s.innerStart, innerEnd: s.innerEnd, innerLength: s.innerLength,
		normal: s.normal, wallThickness: s.wallThickness,
		claimantRoomIDs: s.claimantRoomIDs,
	}));
	const result = BindOpeningsToFloorWalls_Pure(faces, candidates);
	return {openingsBySegment: result.openingsBySegment, warnings: result.warnings};
});

/** The `BoundOpening` for one authored opening object (or null when unbound). The ModUI readouts
 *  (`Door_ModUI`/`Window_ModUI`/`Opening_ModUI`) use this to show "bound to wall segment N at X m".
 *  Set2 F3: the binding is now the FLOOR-level one, so a door on a shared wall reports the merged
 *  segment — the same segment the single merged wall solid is punched on. */
export const ResolveOpeningBinding = CreateAccessor((openingObj: MapObject): {
	binding: BoundOpening;
	segment: FloorWallSegment;
	ownerRoomObjID: string;
}|n=>{
	const roomOwner = FindOwningRoomOf(openingObj);
	const buildingOwner = FindOwningBuilding(openingObj);
	if (roomOwner == null || buildingOwner == null) return null;
	const buildingObj = buildingOwner.obj;

	// Locate the floor this room sits on, then that floor's plan (rooms + merged walls).
	const floorsRes = ResolveBuildingFloors(buildingObj);
	for (const floor of floorsRes.floors.values()) {
		if (floor.levelObjID == "") continue;
		const levelObj = GetMapObject(floor.levelObjID, buildingObj.map);
		if (levelObj == null) continue;
		const rooms = CollectRoomsForLevel(levelObj);
		if (!rooms.some(r=>r.id == roomOwner.id)) continue;

		const wallPlan = MergeFloorWalls_Pure({rooms: rooms.map(r=>{
			const rings = ResolveRoomWallRings(r, buildingObj);
			return rings == null ? null : {roomObjID: r.id, centerline: rings.centerline, wallThickness: rings.wallThickness};
		}).filter((x): x is {roomObjID: string, centerline: Vec2Simple[], wallThickness: number}=>x != null)});
		const res = ResolveOpeningsForFloor(rooms, buildingObj, wallPlan);
		for (const group of res.openingsBySegment) {
			const hit = group.openings.find(op=>op.objID == openingObj.id);
			if (hit != null) {
				const seg = wallPlan.segments[group.segmentIndex];
				if (seg == null) continue;
				return {binding: hit, segment: seg, ownerRoomObjID: roomOwner.id};
			}
		}
		return null; // the owning room's floor was found but the opening is unbound
	}
	return null;
});

/** Lightweight nearest-ancestor-or-self Room owner walk (mirrors `FindOwningRoom` without a
 *  circular import). Returns the owning Room object or null. */
function FindOwningRoomOf(obj: MapObject): MapObject|n {
	const chain = [...GetObjAncestors(obj), obj];
	for (let i = chain.length - 1; i >= 0; i--) {
		const anc = chain[i];
		const mod = GetEnabledMod(anc, "room");
		if (mod) return anc;
	}
	return null;
}

// ---------------------------------------------------------------------------
// §3.7 Geometry plan
// ---------------------------------------------------------------------------

/** Convert a room's rectilinear footprint (already building-local, CCW + closed) into an
 *  axis-aligned `RoofRect` (the §5.1 union input). */
function FootprintToRoofRect(points: Vec2Simple[]): RoofRect {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const [x, y] of points) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	return {minX, minY, maxX, maxY};
}

/** Build the §5 input (per-floor rects + wallTopZ) for a building's roof, from its resolved
 *  floors. Only rooms with `roofContribution != "none"` contribute. Building-local frame. */
export const ResolveBuildingRoofInput = CreateAccessor((buildingObj: MapObject): ResolveRoofPlan_Input|n=>{
	const {floors} = ResolveBuildingFloors(buildingObj);
	const rectsByFloor = new Map<number, RoofRect[]>();
	const wallTopZByFloor = new Map<number, number>();
	let anyRoom = false;
	for (const floor of floors.values()) {
		const levelObj = floor.levelObjID ? GetMapObject(floor.levelObjID, buildingObj.map) : null;
		if (levelObj == null) continue;
		const rects: RoofRect[] = [];
		for (const roomObj of CollectRoomsForLevel(levelObj)) {
			const roomMod = GetEnabledMod(roomObj, "room") as Mod_Room|n;
			if (roomMod?.roofContribution == RoomRoofContribution.none) continue;
			const roofFootprint = ResolveRoomFootprintForRoof(roomObj, buildingObj);
			if (roofFootprint == null || roofFootprint.length < 3) continue;
			rects.push(FootprintToRoofRect(roofFootprint));
			anyRoom = true;
		}
		if (rects.length) {
			rectsByFloor.set(floor.floorNumber, rects);
			wallTopZByFloor.set(floor.floorNumber, floor.wallTopZ);
		}
	}
	if (!anyRoom) return null;
	return {rectsByFloor, wallTopZByFloor, params: ResolveRoofParams(buildingObj)};
});

/** Resolve the building's P5 roof plan (clusters + MeshPlan + warnings). Returns null when the
 *  building's roof is disabled or there are no contributing rooms. */
export const ResolveBuildingRoof = CreateAccessor((buildingObj: MapObject): {
	clusters: RoofCluster[];
	mesh: MeshPlan;
	warnings: string[];
}|n=>{
	const mod = GetEnabledMod(buildingObj, "building") as Mod_Building|n;
	if (mod?.roofEnabled == false) return null;
	const input = ResolveBuildingRoofInput(buildingObj);
	if (input == null) return null;
	return ResolveRoofPlan_Pure(input);
});

/** Resolve the building's geometry plan (P5 adds the roof clusters). Set2 F3 §8: the loop is
 *  `floors → {merge the floor's walls, bind the floor's openings, then per room collect its
 *  slab/ceiling instance}`, and the §7 geometry key moves to the FLOOR. */
export const ResolveBuildingGeometryPlan = CreateAccessor((buildingObj: MapObject): BuildingGeometryPlan|n=>{
	const worldPos = ResolveObjPosInWorld(buildingObj);
	if (worldPos == null) return null;
	const worldRotationDeg = ResolveObjRotInWorld(buildingObj) ?? 0;

	const {floors, warnings} = ResolveBuildingFloors(buildingObj);
	const planFloors: BuildingFloorPlan[] = [];
	const allWarnings = [...warnings];

	// Highest occupied floor number (a floor with a level that owns rooms) — used to detect the
	// TOP occupied floor, which is the only floor that emits a dedicated ceiling plane (interior
	// floors get their ceiling from the floor slab above's underside).
	const occupiedFloorNumbers: number[] = [];
	for (const floor of floors.values()) {
		const levelObj = floor.levelObjID ? GetMapObject(floor.levelObjID, buildingObj.map) : null;
		if (levelObj == null) continue;
		occupiedFloorNumbers.push(floor.floorNumber);
	}
	const maxOccupiedFloorNumber = occupiedFloorNumbers.length ? Math.max(...occupiedFloorNumbers) : -Infinity;

	for (const floor of floors.values()) {
		// gap-bridged floors (levelObjID "") have no rooms.
		const levelObj = floor.levelObjID ? GetMapObject(floor.levelObjID, buildingObj.map) : null;
		if (levelObj == null) continue;

		const floorRooms = CollectRoomsForLevel(levelObj);

		// --- §3 floor wall MERGE (F2 engine) over this floor's rooms.
		const wallInputs = floorRooms.map(r=>{
			const rings = ResolveRoomWallRings(r, buildingObj);
			if (rings == null) return null;
			return {
				roomObjID: r.id,
				centerline: rings.centerline,
				wallThickness: rings.wallThickness,
				// The authored outline, so the 2D overlay can draw the centerline reference line.
				sourceRing: ResolveRoomFootprint_InBuilding(r, buildingObj)?.points ?? rings.centerline,
			};
		}).filter((x): x is {roomObjID: string, centerline: Vec2Simple[], wallThickness: number, sourceRing: Vec2Simple[]}=>x != null);
		const walls = wallInputs.length ? MergeFloorWalls_Pure({rooms: wallInputs}) : null;
		if (walls != null) allWarnings.push(...walls.warnings);

		// --- §5 floor-level opening binding (against the merged segments' INTERIOR faces).
		const openingsRes = ResolveOpeningsForFloor(floorRooms, buildingObj, walls);
		allWarnings.push(...openingsRes.warnings);

		// --- Per-room slab/ceiling/finish instances.
		const hasFloorAbove = floor.floorNumber < maxOccupiedFloorNumber;
		const rooms: BuildingRoomInstance[] = [];
		for (const roomObj of floorRooms) {
			const rings = ResolveRoomWallRings(roomObj, buildingObj);
			if (rings == null) continue;
			const params = ResolveRoomParams(roomObj);
			// wallHeight is a LEVEL-slice field (a Room can override it via the fallback chain —
			// `MapModifierToDefaultsSource` maps room.wallHeight into the level slice), so it
			// resolves through ResolveLevelParams for this room.
			const levelParams = ResolveLevelParams(roomObj);
			const materials = ResolveBuildingMaterials(roomObj) as unknown as ResolvedRoomMaterials;
			const wallTopZ = levelParams.wallHeight;
			const ceilingZ = params.hasCeiling ? levelParams.wallHeight : null;
			// §3.3 step 8: the room's INNER FACE comes from the floor merge (flush on shared edges),
			// falling back to the per-room ring if this room was filtered out of the merge.
			const innerFace = walls?.innerFacePerRoom.find(p=>p.roomObjID == roomObj.id)?.ring ?? rings.innerRing;
			// §6.2 fix: the SLAB ring (flush at shared edges, expanded to the outer face on exterior
			// ones) so the slab supports the full exterior wall. Same fallback shape.
			const slabRing = walls?.slabRingPerRoom.find(p=>p.roomObjID == roomObj.id)?.ring ?? rings.outerRing;

			rooms.push({
				roomObjID: roomObj.id,
				innerFace,
				slabRing,
				wallThickness: rings.wallThickness,
				floorZ: floor.floorTopZ, // rooms sit on the floor slab top
				floorThickness: levelParams.floorThickness,
				ceilingZ,
				hasFloor: params.hasFloor,
				hasCeiling: params.hasCeiling,
				hasFloorAbove,
				wallBaseZ: 0, // relative to floorZ (slab top)
				wallTopZ,
				materials,
			});
		}

		const floorPlan: BuildingFloorPlan = {
			floorNumber: floor.floorNumber,
			baseZ: floor.baseZ,
			floorTopZ: floor.floorTopZ,
			wallTopZ: floor.wallTopZ,
			walls,
			openingsBySegment: openingsRes.openingsBySegment,
			rooms,
			geometryKey: "",
		};
		floorPlan.geometryKey = ComputeFloorGeometryKey(floorPlan);
		// §11 / F4: a floor's materials are resolved from its FIRST room by the renderer, so a
		// multi-room floor whose rooms disagree on a wall material would silently ignore all but one.
		// Surface it the same way the thickness conflict is surfaced (the geometry is unaffected).
		allWarnings.push(...DetectFloorMaterialConflicts_Pure(rooms.map(r=>({roomObjID: r.roomObjID, materials: r.materials}))));
		planFloors.push(floorPlan);
	}

	// P5 roof: resolve clusters + append their warnings.
	const roofWarnings: string[] = [];
	const roof = ResolveBuildingRoof(buildingObj);
	if (roof != null) roofWarnings.push(...roof.warnings);

	return {
		buildingObjID: buildingObj.id,
		worldOrigin: [worldPos.x, worldPos.y, worldPos.z],
		worldRotationDeg,
		floors: planFloors,
		roof: roof?.clusters ?? [],
		warnings: [...allWarnings, ...roofWarnings],
	};
});

/** Resolve a room's building-local wall rings under the CENTERLINE convention (Set2 §2): the authored/resolved outline IS the wall centerline, and the two faces are it offset by ∓`wallThickness/2`. Returns null when the footprint is unresolvable OR the t/2 inset degenerates — the caller then skips the room rather than emitting a zero/inverted-thickness wall. Thickness resolves via `ResolveRoomParams().wallThickness`, i.e. the full §3.3 chain including `Mod_Level.wallThickness`.
 *  Set1's `ResolveRoomOuterRing` produced the interior face + `footprint + wallThickness`; Set2 keeps the same three-ring shape but re-anchors the middle ring. */
export const ResolveRoomWallRings = CreateAccessor((roomObj: MapObject, buildingObj: MapObject): {
	centerline: Vec2Simple[];
	innerRing: Vec2Simple[];
	outerRing: Vec2Simple[];
	wallThickness: number;
}|n=>{
	const inBuilding = ResolveRoomFootprint_InBuilding(roomObj, buildingObj);
	if (inBuilding == null) return null;
	const params = ResolveRoomParams(roomObj);
	const wallThickness = params.wallThickness;
	const rings = CenterlineToWallRings(inBuilding.points, wallThickness);
	if (rings == null) return null;
	return {...rings, wallThickness};
});

/** Set2 §7: build the FLOOR's geometry key — a hash of everything that affects the floor's *shape*
 *  (the merged wall segments, the floor's openings, the room finish rings, the storey wall height
 *  and slab thickness) but NOT the Z offset, so the identical floors of a 100-storey tower share
 *  one set of BufferGeometries. Note the wall height enters as `wallTopZ − floorTopZ` (a
 *  double-height atrium floor must not share with a normal floor).
 *
 *  F4: the implementation moved to the PURE `BuildingGeometryKey_Pure.ts` so the Node lane can audit
 *  its collisions (this module imports mobx-graphlink, so nothing defined here is testable). */
export {ComputeFloorGeometryKey};

/** Collect the enabled Room mods in `levelObj`'s subtree that are NOT inside a nested
 *  non-prototype Building (nested buildings own their own rooms). */
export const CollectRoomsForLevel = CreateAccessor((levelObj: MapObject): MapObject[]=>{
	const rooms: MapObject[] = [];
	// Include the host object itself in the subtree: a Room can live directly on the same
	// MapObject as the Level/Building (the common single-room case, §3.1 implicit level).
	const subtree = [levelObj, ...GetObjDescendants(levelObj)];
	for (const desc of subtree) {
		const mod = GetEnabledMod(desc, "room");
		if (mod == null) continue;
		const building = FindOwningBuilding(desc);
		if (building != null && building.obj.id != FindOwningBuilding(levelObj)?.obj.id) continue; // nested building
		rooms.push(desc);
	}
	return rooms;
});
